import { useState, useCallback, useRef } from 'react';

// ブラウザとNode.js環境の両方で動作するタイマー型
type TimerId = ReturnType<typeof setTimeout>;

export type ToastType = 'info' | 'warning' | 'error' | 'success';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
  guidance?: string;
  timestamp: number;
}

interface ToastOptions {
  duration?: number;
  guidance?: string;
  [key: string]: any;
}

interface UseToastReturn {
  toasts: Toast[];
  addToast: (toast: Partial<Toast> & { message: string }) => number;
  removeToast: (id: number) => void;
  clearToasts: () => void;
  showSuccess: (message: string, options?: ToastOptions) => number;
  showError: (message: string, options?: ToastOptions) => number;
  showWarning: (message: string, options?: ToastOptions) => number;
  showInfo: (message: string, options?: ToastOptions) => number;
  showToast: (message: string, type: ToastType, options?: ToastOptions) => number;
}

/**
 * トースト通知を管理するカスタムフック
 * @returns {UseToastReturn} トースト管理機能
 */
export const useToast = (): UseToastReturn => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef<number>(0);
  const timeoutRefs = useRef<Map<number, TimerId>>(new Map());

  /**
   * 新しいトーストを追加
   * @param {Partial<Toast> & { message: string }} toast - トーストの設定
   */
  const addToast = useCallback((toast: Partial<Toast> & { message: string }): number => {
    const id = ++toastIdRef.current;
    const newToast: Toast = {
      id,
      message: toast.message,
      type: toast.type || 'info',
      duration: toast.duration || 5000,
      guidance: toast.guidance,
      timestamp: Date.now()
    };

    setToasts((prev) => [...prev, newToast]);

    // 自動削除
    if (newToast.duration > 0) {
      const timeoutId = setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
      timeoutRefs.current.set(id, timeoutId);
    }

    return id;
  }, []);

  /**
   * トーストを削除
   * @param {number} id - 削除するトーストのID
   */
  const removeToast = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));

    // タイムアウトをクリア
    const timeoutId = timeoutRefs.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutRefs.current.delete(id);
    }
  }, []);

  /**
   * 全てのトーストを削除
   */
  const clearToasts = useCallback((): void => {
    setToasts([]);

    // 全てのタイムアウトをクリア
    timeoutRefs.current.forEach((timeoutId) => clearTimeout(timeoutId));
    timeoutRefs.current.clear();
  }, []);

  /**
   * 便利メソッド: 成功メッセージ
   */
  const showSuccess = useCallback(
    (message: string, options: ToastOptions = {}): number => {
      return addToast({
        message,
        type: 'success',
        ...options
      });
    },
    [addToast]
  );

  /**
   * 便利メソッド: エラーメッセージ
   */
  const showError = useCallback(
    (message: string, options: ToastOptions = {}): number => {
      return addToast({
        message,
        type: 'error',
        duration: 7000, // エラーは少し長めに表示
        ...options
      });
    },
    [addToast]
  );

  /**
   * 便利メソッド: 警告メッセージ
   */
  const showWarning = useCallback(
    (message: string, options: ToastOptions = {}): number => {
      return addToast({
        message,
        type: 'warning',
        duration: 6000,
        ...options
      });
    },
    [addToast]
  );

  /**
   * 便利メソッド: 情報メッセージ
   */
  const showInfo = useCallback(
    (message: string, options: ToastOptions = {}): number => {
      return addToast({
        message,
        type: 'info',
        ...options
      });
    },
    [addToast]
  );

  /**
   * 汎用メソッド: トーストを表示
   */
  const showToast = useCallback(
    (message: string, type: ToastType, options: ToastOptions = {}): number => {
      return addToast({
        message,
        type,
        ...options
      });
    },
    [addToast]
  );

  return {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showToast
  };
};
